import { apiRequest } from './http';
import type {
  AdminUser,
  AdminUserPatchBody,
  AdminUsersResponse,
  RoleCreateBody,
  RoleDefinition,
  RoleListResponse,
  RoleMenuLevel,
  RoleMenuMapResponse,
  RoleUpdateBody,
} from './types';

// 감사 로그 (P1-LOG-004) — 권한·토큰 변경 이벤트의 DB 영구 기록 조회. admin 전용.
export interface AuditEvent {
  id: number;
  event_type: string;
  actor_email: string | null;
  target: string | null;
  payload: Record<string, unknown>;
  created_at: string | null;
}

export function fetchAuditEvents(
  q: string,
  signal?: AbortSignal,
): Promise<{ events: AuditEvent[] }> {
  return apiRequest('/admin/audit-events', { query: { q: q || undefined, limit: 200 }, signal });
}

// §5.20 GET /api/admin/users
export interface AdminUsersQuery {
  // 'admin' | 'none'(guest) | 역할 키 | '전체'
  role?: string;
  department?: string;
  q?: string;
}

export function fetchAdminUsers(
  query: AdminUsersQuery = {},
  signal?: AbortSignal,
): Promise<AdminUsersResponse> {
  return apiRequest<AdminUsersResponse>('/admin/users', {
    query: query as { [key: string]: unknown },
    signal,
  });
}

// §5.21 PATCH /api/admin/users/{email}
export function updateAdminUser(email: string, body: AdminUserPatchBody): Promise<AdminUser> {
  return apiRequest<AdminUser>(`/admin/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body,
  });
}

// admin 전용: 역할 정의 목록 (leader/member + 커스텀).
export function fetchRoles(signal?: AbortSignal): Promise<RoleListResponse> {
  return apiRequest<RoleListResponse>('/admin/roles', { signal });
}

export function createRole(body: RoleCreateBody): Promise<RoleDefinition> {
  return apiRequest<RoleDefinition>('/admin/roles', { method: 'POST', body });
}

export function updateRole(key: string, body: RoleUpdateBody): Promise<RoleDefinition> {
  return apiRequest<RoleDefinition>(`/admin/roles/${encodeURIComponent(key)}`, { method: 'PATCH', body });
}

// 커스텀 역할 삭제 — 부여·메뉴 매핑도 함께 지워진다.
export function deleteRole(key: string): Promise<{ deleted: string; unassigned_users: number }> {
  return apiRequest(`/admin/roles/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

// admin 전용: 역할 → 메뉴 레벨 매핑.
export function fetchRoleMaps(signal?: AbortSignal): Promise<RoleMenuMapResponse> {
  return apiRequest<RoleMenuMapResponse>('/admin/role-maps', { signal });
}

// admin 전용: 역할 하나의 메뉴 매핑 일괄 반영. 값 null이면 행 삭제(역할 기본값 복귀).
export function updateRoleMap(
  role: string,
  levels: Record<string, RoleMenuLevel | null>,
): Promise<RoleMenuMapResponse> {
  return apiRequest<RoleMenuMapResponse>(`/admin/role-maps/${encodeURIComponent(role)}`, {
    method: 'PUT',
    body: { levels },
  });
}

// admin 전용: BigQueryAppsProvider in-memory cache(BIGQUERY_APPS_TABLE 결과) 강제 invalidate.
// BQ에 직접 INSERT/UPDATE한 row를 5분 TTL 만료를 기다리지 않고 즉시 보기 위함.
export function refreshAppsCache(): Promise<{ refreshed: boolean }> {
  return apiRequest<{ refreshed: boolean }>('/admin/refresh-cache', { method: 'POST' });
}

export interface AdminFollowupsRunResponse {
  apps_cache_refreshed: boolean;
  app_followups_triggered: boolean;
  app_followups_dry_run: boolean;
  app_followups_stages: string[];
  partners_refresh_job_id: string | null;
}

// admin 전용: utils.apps 후속 파이프라인 수동 실행.
export function runAdminFollowups(): Promise<AdminFollowupsRunResponse> {
  return apiRequest<AdminFollowupsRunResponse>('/admin/followups/run', { method: 'POST' });
}
